import { getLong, setLong, type Storage } from '../platform/storage'
import type { WakeSchedule } from '../core/schedules'
import { fromStored, toStored, type StoredPhase } from './phases'
import { KEY } from './keys'

/**
 * Добавленные ВБ (2…N) для программы: JSON `[{ name, phases }]`, фазы в том же формате, что
 * правки «Моего дня», — его же читают Android и часы. ВБ 1 живёт в `custom_phases_…`.
 *
 * Читаем и пишем через длинные значения: пять карточек с фазами не влезают в одну ячейку
 * настроек, и ответ сервера уже раскладывался по частям. Читать такое обычным способом —
 * значит получить обрывок и ничего не разобрать: именно поэтому у человека не появлялись
 * окна бодрствования, хотя на сервере и на устройстве они были (разбор 28.10.2026).
 */

interface StoredSchedule {
  name: string
  phases: StoredPhase[]
  /** Сон перед этим ВБ, минуты; Kotlin поле не читает — ему нужны только фазы. */
  napBefore?: number
}

export async function loadSchedules(storage: Storage, programId: string): Promise<WakeSchedule[]> {
  const raw = await getLong(storage, KEY.wakeSchedules(programId))
  if (raw === null) return []
  return parseSchedules(raw)
}

/** Разбор JSON списка ВБ; повреждённые записи пропускаются. */
export function parseSchedules(raw: string): WakeSchedule[] {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const out: WakeSchedule[] = []
    for (const item of parsed as Array<Partial<StoredSchedule>>) {
      if (typeof item?.name !== 'string' || !Array.isArray(item.phases)) continue
      const phases = item.phases.map(fromStored)
      if (phases.length === 0 || phases.some((p) => p === null)) continue
      const nap = typeof item.napBefore === 'number' && item.napBefore > 0 ? Math.round(item.napBefore) : undefined
      out.push({ name: item.name, phases: phases as WakeSchedule['phases'], ...(nap === undefined ? {} : { napBefore: nap }) })
    }
    return out
  } catch {
    return []
  }
}

export function serializeSchedules(list: WakeSchedule[]): string {
  const stored: StoredSchedule[] = list.map((s) => ({ name: s.name, phases: s.phases.map(toStored), ...(s.napBefore === undefined ? {} : { napBefore: s.napBefore }) }))
  return JSON.stringify(stored)
}

export async function saveSchedules(storage: Storage, programId: string, list: WakeSchedule[], now = Date.now()): Promise<void> {
  // Пустой список: сперва стираем хвосты длинного значения, затем сам ключ
  if (list.length === 0) { await setLong(storage, KEY.wakeSchedules(programId), ''); await storage.remove(KEY.wakeSchedules(programId)) }
  else await setLong(storage, KEY.wakeSchedules(programId), serializeSchedules(list))
  await storage.set(KEY.wakeSchedulesUpdatedAt(programId), String(now))
}
